import { ZuiFormAssociatedElement } from '@zywave/zui-base';
import '@zywave/zui-icons';
import { html } from 'lit';
import { property, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { style } from './zui-input-file-css.js';
import type { PropertyValues } from 'lit';
import type { ValidationMessageMap } from '@zywave/zui-base';

const VALIDATION_MESSAGE_MAP: ValidationMessageMap = {
  valueMissing: 'required',
};

/**
 * A standardized component modelled after native file input.
 * @element zui-input-file
 *
 * @attr {string | null} [name=null] - The name of this element that is associated with form submission
 * @attr {boolean} [disabled=false] - Represents whether a user can make changes to this element; if true, the value of this element will be excluded from the form submission
 * @attr {boolean} [readonly=false] - Represents whether a user can make changes to this element; the value of this element will still be included in the form submission
 * @attr {boolean} [autofocus=false] - If true, this element will be focused when connected to the document
 * @attr {string} [validation-message-required] - The message to display when the value is required but not provided
 *
 * @prop {string | null} [name=null] - The name of this element that is associated with form submission
 * @prop {boolean} [disabled=false] - Represents whether a user can make changes to this element; if true, the value of this element will be excluded from the form submission
 * @prop {boolean} [readOnly=false] - Represents whether a user can make changes to this element; the value of this element will still be included in the form submission
 * @prop {boolean} [autofocus=false] - If true, this element will be focused when connected to the document
 * @prop {boolean} willValidate - Returns true if the element will be validated when the form is submitted
 * @prop {ValidityState} validity - Returns a {@link https://developer.mozilla.org/en-US/docs/Web/API/ValidityState | ValidityState} object that represents the validity states of an element
 * @prop {string} validationMessage - Returns the error message that would be displayed if the element was to be checked for validity
 *
 * @event {CustomEvent} change - Event fired when file has been uploaded by the user
 */
export class ZuiInputFile extends ZuiFormAssociatedElement {
  // Start FACE related
  protected get _focusControlSelector(): string {
    return 'input';
  }

  protected get _formValue(): FileList {
    const input = this._hiddenInput as HTMLInputElement;
    return input?.files;
  }

  protected formResetCallback() {
    this.reset();
  }
  // End FACE related

  static get styles() {
    return [super.styles, style];
  }

  /**
   * A `get` accessor for retrieving files, after they're uploaded
   */
  get files(): FileList {
    return this.#files;
  }

  @query('input')
  private _hiddenInput: HTMLInputElement;

  @query('.container')
  private _containerEl: HTMLInputElement;

  /**
   * Text for button portion of component
   */
  @property({ type: String, attribute: 'button-text' })
  buttonText = '';

  /**
   * Acceptable file types, see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/file#accept
   */
  @property({ type: String, attribute: 'accept' })
  accept = '';

  /**
   * Placeholder or ghost text on initial load
   */
  @property({ type: String, attribute: 'placeholder' })
  placeholder = '';

  /**
   * Determines whether this form control is required
   */
  @property({ type: Boolean })
  required = false;

  /**
   * Set to `true` if hiding the `upload` icon is preferred
   */
  @property({ type: Boolean, attribute: 'no-icon' })
  noIcon = false;

  #files: FileList;

  get #formValueFirstFile(): File | undefined {
    return this._formValue?.[0];
  }

  connectedCallback() {
    super.connectedCallback();
    for (const eventName of this.#dragAndDropEventsList) {
      this.addEventListener(eventName, this.#handleDragAndDropEvent, false);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const event in this.#dragAndDropEventsList) {
      this.removeEventListener(event, this.#handleDragAndDropEvent);
    }
  }

  firstUpdated(changedProps: PropertyValues<this>) {
    super.firstUpdated(changedProps);

    this.#files = this._hiddenInput.files;
    this._setFormValue(this._formValue);
  }

  updated(changedProps: PropertyValues<this>) {
    super.updated(changedProps);

    this.#setValidity();
  }

  render() {
    const classes = { 'value-text-color': this.#formValueFirstFile?.name ? true : false };
    return html`
      <div
        tabindex="${this.disabled ? '-1' : '0'}"
        class="container ${classMap({ 'no-icon': this.noIcon, disabled: this.disabled })}"
        @click="${this.#clickHiddenInput}"
        @keyup="${this.#handleKeyEvent}"
      >
        <div class="button ${classMap({ disabled: this.disabled })}">
          <zui-icon class="small" icon="zui-upload"></zui-icon>
          <span class="button-text">${this.buttonText}</span>
        </div>
        <input
          class="hidden"
          tabindex="-1"
          type="file"
          accept="${this.accept}"
          placeholder="${this.placeholder}"
          @change="${this.#onChange}"
          ?required="${this.required}"
          ?disabled="${this.disabled}"
        />
        <div class="faux-input ${classMap({ disabled: this.disabled })}">
          ${this.#determineFauxInputText()
            ? html` <span class="${classMap(classes)}">${this.#determineFauxInputText()}</span> `
            : html``}
        </div>
      </div>
    `;
  }

  /**
   * Resets the form value
   */
  reset() {
    (this._hiddenInput as HTMLInputElement).value = '';
    this.#files = this._hiddenInput.files!;
    this._setFormValue(null);
    this.requestUpdate();
  }

  #determineFauxInputText() {
    return this.#formValueFirstFile?.name ?? this.placeholder;
  }

  #clickHiddenInput() {
    this._hiddenInput.click();
  }

  #handleKeyEvent(event: KeyboardEvent) {
    if (event.code === 'Enter' || event.code === 'Space') {
      this.#clickHiddenInput();
    }
  }

  #onChange(e: Event) {
    this.#files = (e.target as HTMLInputElement).files;
    this.#addFile();
    this.requestUpdate();
  }

  #addFile() {
    // this._setFormValue is a FACE method
    this._setFormValue(this._formValue);
    const payload = new FileChangeEventDetailShim(this.#files);
    this.dispatchEvent(new CustomEvent<FileInputChangeEventDetail>('change', { detail: payload, bubbles: true }));
  }

  #dragAndDropEventsList = ['dragenter', 'dragover', 'dragleave', 'drop'];

  #handleDragAndDropEvent(e: DragEvent) {
    if (this.disabled) {
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      this._containerEl?.classList.add('has-file-dragover');
    } else {
      this._containerEl?.classList.remove('has-file-dragover');

      if (e.type === 'drop') {
        const dt = (e as DragEvent).dataTransfer;
        const files = dt.files;

        if (files.length > 1) {
          return;
        }

        this.#files = files;
        this._hiddenInput.files = files;
        this.#addFile();
        this.requestUpdate();
      }
    }
  }

  #setValidity() {
    const input = this._hiddenInput;
    if (!input) {
      return;
    }

    const inputValidity = input.validity;
    const validity: ValidityStateFlags = {
      valueMissing: inputValidity.valueMissing,
      customError: this.validity.customError,
    };

    let message = '';
    if (validity.customError) {
      message = this.validationMessage;
    } else {
      message = this._mapValidationMessage(input.validationMessage, validity, VALIDATION_MESSAGE_MAP);
    }

    this._setValidity(validity, message);
  }
}

window.customElements.define('zui-input-file', ZuiInputFile);

declare global {
  interface HTMLElementTagNameMap {
    'zui-input-file': ZuiInputFile;
  }
}

// this class and its inheritance structure exists purely as backwards compatibility for those relying on the details property directly being a File
class FileChangeEventDetailShim extends File {
  files: FileList;

  /**
   * @deprecated - Using the event detail as a File directly is deprecated and will be removed. Use the files property instead to access the file.
   */
  readonly lastModified: number;
  /**
   * @deprecated - Using the event detail as a File directly is deprecated and will be removed. Use the files property instead to access the file.
   */
  readonly name: string;
  /**
   * @deprecated - Using the event detail as a File directly is deprecated and will be removed. Use the files property instead to access the file.
   */
  readonly webkitRelativePath: string;
  /**
   * @deprecated - Using the event detail as a File directly is deprecated and will be removed. Use the files property instead to access the file.
   */
  readonly size: number;
  /**
   * @deprecated - Using the event detail as a File directly is deprecated and will be removed. Use the files property instead to access the file.
   */
  readonly type: string;
  /**
   * @deprecated - Using the event detail as a File directly is deprecated and will be removed. Use the files property instead to access the file.
   */
  arrayBuffer: () => Promise<ArrayBuffer>;
  /**
   * @deprecated - Using the event detail as a File directly is deprecated and will be removed. Use the files property instead to access the file.
   */
  slice: (start?: number, end?: number) => Blob;
  /**
   * @deprecated - Using the event detail as a File directly is deprecated and will be removed. Use the files property instead to access the file.
   */
  stream: () => ReadableStream;
  /**
   * @deprecated - Using the event detail as a File directly is deprecated and will be removed. Use the files property instead to access the file.
   */
  text: () => Promise<string>;

  constructor(files: FileList) {
    const file = files[0];
    super([file], file.name, { type: file.type, lastModified: file.lastModified });

    this.files = files;
  }
}

export type FileInputChangeEventDetail = InstanceType<typeof FileChangeEventDetailShim>;
